import React, { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuthStore } from './stores/authStore';
import { AuthPage } from './components/auth/AuthPage';
import { UserMenu } from './components/auth/UserMenu';
import Header from './components/layout/Header';
import ParticleBackground from './components/ParticleBackground';
import LiveSpeedCam from './components/panels/LiveSpeedCam';
import ChartCarousel from './components/panels/ChartCarousel';
import { FloatingTools } from './components/FloatingTools';
// ARCHIVED: import GuidanceOverlay from './components/GuidanceOverlay';
import ToolsPage from './pages/ToolsPage';
import VaultPage from './pages/VaultPage';
import ClaudePage from './pages/ClaudePage';
import { ClientsPage } from './pages/ClientsPage';
import { FeedbackPage } from './pages/FeedbackPage';
import { LightSitePage } from './pages/LightSitePage';
import { ProjectsPage } from './pages/ProjectsPage';
import { ExportsPage } from './pages/ExportsPage';
import { SettingsPage } from './pages/SettingsPage';
// ARCHIVED: import { PipelineConfigPage } from './pages/PipelineConfigPage';
import { FixReviewPage } from './pages/FixReviewPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { AgentBuilderPage } from './pages/AgentBuilderPage';
// import { PipelineBuilderPage } from './pages/PipelineBuilderPage';
import { TaskHistoryPage } from './pages/TaskHistoryPage';
import ConversationsPage from './pages/ConversationsPage';
import AITestingPage from './pages/AITestingPage';
import KnowledgeBasePage from './pages/KnowledgeBasePage';
import DropGenPage from './pages/DropGenPage';
import { LightGrid } from './components/tools/LightGrid';
import ClaudeLogsPage from './pages/ClaudeLogsPage';
import TerminalPage from './pages/TerminalPage';
import IBVApprovals from './components/IBVApprovals';
import { SynapseWebSocketProvider } from './contexts/SynapseWebSocketContext';
import { AssemblyLine } from './components/panels/AssemblyLine';
import { TaskInput } from './components/panels/TaskInput';
import SubAgentPanel from './components/panels/SubAgentPanel';

// Assembly Line Page - full page animated factory view
const AssemblyLinePage: React.FC = () => (
  <main className="flex-1 p-6 overflow-y-auto">
    <div className="mb-6">
      <h1 className="text-2xl font-bold text-white mb-2">Assembly Line</h1>
      <p className="text-gray-400">Factory-style pipeline visualization</p>
    </div>
    <TaskInput />
    <AssemblyLine isRunning={true} refreshInterval={10000} />
  </main>
);

// Dashboard content component - AssemblyLine at top, SubAgentPanel, then chart carousel
const DashboardContent: React.FC = () => (
  <main className="flex-1 p-4 overflow-y-auto">
    <LiveSpeedCam>
      {/* Assembly Line Pipeline with integrated task input */}
      <div style={{ marginBottom: '24px' }}>
        <AssemblyLine isRunning={true} refreshInterval={10000} />
      </div>

      {/* SubAgent Panel - Launch and manage agents */}
      <div style={{ marginBottom: '24px', padding: '0 12px' }}>
        <SubAgentPanel />
      </div>

      {/* Chart Carousel - 6 charts, 3x2 grid */}
      <div style={{ padding: '0 12px' }}>
        <ChartCarousel />
      </div>
    </LiveSpeedCam>
  </main>
);

const App: React.FC = () => {
  const location = useLocation();
  const { initialize, initialized, isAuthenticated } = useAuthStore();
  const [isAdmin, setIsAdmin] = React.useState(true);

  // Check if current route should hide navigation
  const hideNav = location.pathname === '/ibv-approvals' || location.pathname === '/dropgen';
  // Only IBV needs sidebar completely hidden - DropGen has its own internal nav
  const hideSidebar = location.pathname === '/ibv-approvals';

  useEffect(() => {
    initialize();
  }, [initialize]);

  // Auth bypass: don't block on Supabase initialization or login
  // Dashboard is private VPS — no need for auth gate
  if (!initialized) {
    // Still try to init, but don't block rendering
    // (useEffect above handles async init)
  }

  return (
    <SynapseWebSocketProvider>
      <div className="min-h-screen bg-[#0d1117] text-white flex flex-col">
        {/* Animated particle background with connecting lines */}
        {!hideNav && <ParticleBackground />}
        {!hideNav && (
          <div className="relative z-[9999]">
            <Header
              userMenu={<UserMenu />}
              isAdmin={isAdmin}
              onToggleAdmin={() => setIsAdmin(!isAdmin)}
            />
          </div>
        )}
      <div className={`flex flex-1 overflow-hidden relative z-10 ${hideSidebar ? 'no-sidebar' : ''}`}>
        <div className={`flex-1 w-full ${!hideNav ? 'pt-14 sm:pt-20' : ''}`}>
        <Routes>
          <Route path="/" element={<DashboardContent />} />
          {isAdmin && (
            <>
              {/* ARCHIVED: <Route path="/pipeline" element={<PipelineConfigPage />} /> */}
              <Route path="/tools" element={<ToolsPage />} />
              <Route path="/vault" element={<VaultPage />} />
              <Route path="/claude" element={<ClaudePage />} />
              <Route path="/clients" element={<ClientsPage />} />
              <Route path="/feedback" element={<FeedbackPage />} />
              <Route path="/lightsite" element={<LightSitePage />} />
              <Route path="/projects" element={<ProjectsPage />} />
              <Route path="/exports" element={<ExportsPage />} />
              <Route path="/settings" element={<SettingsPage />} />
              <Route path="/notifications" element={<NotificationsPage />} />
              <Route path="/agents" element={<AgentBuilderPage />} />
              {/* <Route path="/blueprints" element={<PipelineBuilderPage />} /> */}
              <Route path="/builder" element={<LightGrid />} />
              <Route path="/fix-review" element={<FixReviewPage />} />
              <Route path="/history" element={<TaskHistoryPage />} />
              <Route path="/assembly" element={<AssemblyLinePage />} />
              <Route path="/conversations" element={<ConversationsPage />} />
              <Route path="/ai-testing" element={<AITestingPage />} />
              <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/claude-logs" element={<ClaudeLogsPage />} />
              <Route path="/terminal" element={<TerminalPage />} />
              <Route path="/ibv-approvals" element={<IBVApprovals />} />
              <Route path="/dropgen" element={<DropGenPage />} />
            </>
          )}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </div>
      </div>
      {isAdmin && <FloatingTools />}
      {/* ARCHIVED: <GuidanceOverlay sessionId="global" /> */}
    </div>
    </SynapseWebSocketProvider>
  );
};

export default App;
